import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isWeekend, addMonths, subMonths } from 'date-fns';
import { Home, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Package, Check, XCircle, AlertTriangle, MessageSquare, Truck, ArrowRightLeft, Clock, CheckCircle } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import GoodsInPOPanel from '../components/GoodsInPOPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { API } from '../App';
import api from '../services/api';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';
import { useLimitedShell } from '../limited/LimitedShellContext';

const enconOrange = '#ee7203';
const enconBlack = '#231f20';
const enconPurple = '#8B5CF6';
const enconPink = '#EC4899'; // Pink for Stock OUT transfers

// Delivery Animation Overlay Component
const DeliveryAnimationOverlay = ({ isVisible, newDeliveryInfo, onAnimationComplete }) => {
  const [phase, setPhase] = useState('entering'); // entering, showing, exiting
  
  useEffect(() => {
    if (isVisible) {
      setPhase('entering');
      
      // Force a reflow to ensure animation triggers
      document.body.offsetHeight;
      
      // Phase 1: Truck enters from right (1.5s)
      const enterTimer = setTimeout(() => setPhase('showing'), 1500);
      
      // Phase 2: Show delivery info (2s)
      const showTimer = setTimeout(() => setPhase('exiting'), 3500);
      
      // Phase 3: Exit and complete (1s)
      const exitTimer = setTimeout(() => {
        onAnimationComplete();
      }, 4500);
      
      return () => {
        clearTimeout(enterTimer);
        clearTimeout(showTimer);
        clearTimeout(exitTimer);
      };
    }
  }, [isVisible, onAnimationComplete]);
  
  if (!isVisible) return null;
  
  // Purple color for gradient
  const enconPurple = '#7c3aed';
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)' // For Safari/Edge
      }}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 50px,
              ${enconOrange}22 50px,
              ${enconOrange}22 100px
            )`,
            animation: 'slide 20s linear infinite'
          }}
        />
      </div>
      
      {/* Pulsing circles effect - Orange and Purple */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Purple circle */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4"
          style={{
            borderColor: enconPurple,
            width: '250px',
            height: '250px',
            opacity: 0.4,
            animation: 'pulse-ring 2s ease-out infinite'
          }}
        />
        {/* Orange circle */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4"
          style={{
            borderColor: enconOrange,
            width: '350px',
            height: '350px',
            opacity: 0.3,
            animation: 'pulse-ring 2s ease-out 0.4s infinite'
          }}
        />
      </div>
      
      {/* Main content container */}
      <div className="relative flex flex-col items-center gap-8">
        
        {/* Animated Delivery Truck - enters from RIGHT */}
        <div 
          className={`relative transition-all duration-1000 ease-out ${
            phase === 'entering' ? 'translate-x-[100vw]' : 
            phase === 'exiting' ? '-translate-x-[100vw]' : 
            'translate-x-0'
          }`}
        >
          {/* Road/Ground */}
          <div 
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-3 rounded-full"
            style={{ backgroundColor: '#333', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
          />
          
          {/* Truck Image - Nevill Long / Encon truck */}
          <img 
            src="/delivery-truck.png"
            alt="Delivery Truck"
            className="drop-shadow-2xl relative z-10"
            style={{ 
              width: '450px', 
              height: 'auto',
              filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))',
              display: 'block',
              marginBottom: '20px'
            }}
            onError={(e) => {
              console.error('Failed to load truck image');
            }}
            onLoad={() => console.log('Truck image loaded successfully')}
          />
          
          {/* Motion lines when entering - on the LEFT side since truck comes from right */}
          {phase === 'entering' && (
            <div className="absolute right-full top-1/2 -translate-y-1/2 flex flex-row-reverse gap-2 opacity-60">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i}
                  className="w-8 h-1 rounded-full"
                  style={{ 
                    backgroundColor: i % 2 === 0 ? enconOrange : enconPurple,
                    opacity: 1 - (i * 0.2),
                    animation: `fadeInOut 0.3s ease-in-out ${i * 0.1}s infinite`
                  }}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* New Delivery Info Card */}
        <div 
          className={`transition-all duration-500 ${
            phase === 'showing' ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <div 
            className="px-12 py-8 rounded-3xl text-center"
            style={{ 
              background: `linear-gradient(135deg, ${enconOrange} 0%, ${enconPurple} 100%)`,
              boxShadow: `0 0 60px ${enconOrange}66, 0 0 100px ${enconPurple}44`
            }}
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <Package className="w-12 h-12 text-white animate-bounce" />
              <h2 
                className="text-4xl md:text-5xl font-black tracking-tight"
                style={{
                  background: `linear-gradient(90deg, ${enconOrange} 0%, ${enconPurple} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: 'none',
                  filter: 'brightness(1.3)'
                }}
              >
                NEW DELIVERY!
              </h2>
              <Package className="w-12 h-12 text-white animate-bounce" />
            </div>
            
            {newDeliveryInfo && (
              <div className="mt-4 space-y-2">
                <p className="text-2xl md:text-3xl font-bold text-white/90">
                  {newDeliveryInfo.supplier}
                </p>
                <p className="text-xl text-white/80">
                  {newDeliveryInfo.order_number}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes slide {
          0% { transform: translateX(0); }
          100% { transform: translateX(100px); }
        }
        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.4; }
          100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
        }
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// PI Count Notification Animation Overlay
const PICountAnimationOverlay = ({ isVisible, onAnimationComplete }) => {
  const [phase, setPhase] = useState('entering');
  
  useEffect(() => {
    if (isVisible) {
      setPhase('entering');
      const enterTimer = setTimeout(() => setPhase('showing'), 800);
      const showTimer = setTimeout(() => setPhase('exiting'), 3800);
      const exitTimer = setTimeout(() => onAnimationComplete(), 4500);
      return () => { clearTimeout(enterTimer); clearTimeout(showTimer); clearTimeout(exitTimer); };
    }
  }, [isVisible, onAnimationComplete]);
  
  if (!isVisible) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(8px)' }}
    >
      {/* Pulsing gold circles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4" style={{ borderColor: '#d4a017', width: '250px', height: '250px', opacity: 0.4, animation: 'pulse-ring 2s ease-out infinite' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4" style={{ borderColor: '#eab308', width: '350px', height: '350px', opacity: 0.3, animation: 'pulse-ring 2s ease-out 0.4s infinite' }} />
      </div>
      
      <div className={`relative flex flex-col items-center gap-6 transition-all duration-700 ${
        phase === 'entering' ? 'opacity-0 scale-50' :
        phase === 'exiting' ? 'opacity-0 scale-150' :
        'opacity-100 scale-100'
      }`}>
        {/* Large notification icon */}
        <div className="relative">
          <AlertTriangle className="w-32 h-32 drop-shadow-2xl" style={{ color: '#eab308', filter: 'drop-shadow(0 0 40px rgba(234,179,8,0.5))' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-36 h-36 rounded-full" style={{ border: '4px solid rgba(234,179,8,0.3)', animation: 'pulse-ring 1.5s ease-out infinite' }} />
          </div>
        </div>
        
        {/* Text */}
        <div className="text-center px-12 py-6 rounded-2xl" style={{ background: 'linear-gradient(135deg, #d4a017 0%, #eab308 100%)', boxShadow: '0 0 60px rgba(234,179,8,0.4)' }}>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">PI Count Received</h2>
          <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-2">Please Action</p>
        </div>
      </div>
      
      <style>{`
        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.4; }
          100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const GoodsInWarehouse = ({ user }) => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [transfers, setTransfers] = useState([]); // Transfer In entries
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [warehouseNote, setWarehouseNote] = useState('');
  const [showFailedWarning, setShowFailedWarning] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [expandedTransferGroup, setExpandedTransferGroup] = useState(null);
  
  // Branch context for data isolation
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();
  const limitedShell = useLimitedShell();
  
  // Branch-aware accent color (purple for E70, orange for others)
  const accentColor = effectiveBranchId === 'E70' ? enconPurple : enconOrange;
  
  // Animation state for new deliveries
  const [showDeliveryAnimation, setShowDeliveryAnimation] = useState(false);
  const [newDeliveryInfo, setNewDeliveryInfo] = useState(null);
  const previousDeliveryIds = useRef(new Set());
  const isFirstLoad = useRef(true);
  
  // Initialize previousDeliveryIds from localStorage for persistence across refreshes
  useEffect(() => {
    try {
      const storedIds = localStorage.getItem('goods_in_delivery_ids');
      if (storedIds) {
        previousDeliveryIds.current = new Set(JSON.parse(storedIds));
        isFirstLoad.current = false; // If we have stored IDs, this isn't a "first load"
      }
    } catch (e) {
      console.log('Could not restore delivery IDs from storage');
    }
  }, []);

  const today = new Date();

  // Get 5 working week dates
  const getWorkingWeekDates = () => {
    const dates = [];
    let currentDate = today;
    while (isWeekend(currentDate)) {
      currentDate = addDays(currentDate, 1);
    }
    let count = 0;
    let checkDate = currentDate;
    while (count < 5) {
      if (!isWeekend(checkDate)) {
        dates.push(checkDate);
        count++;
      }
      checkDate = addDays(checkDate, 1);
    }
    return dates;
  };

  const workingWeekDates = getWorkingWeekDates();

  // Fetch deliveries with new entry detection
  const fetchDeliveries = useCallback(async () => {
    if (!isFirstLoad.current) {
      // Don't show loading spinner on subsequent fetches (for TV display)
    } else {
      setLoading(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      // Include branch parameter for data isolation
      const url = effectiveBranchId 
        ? `${API}/goods-in/deliveries?view_as_branch=${effectiveBranchId}`
        : `${API}/goods-in/deliveries`;
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newDeliveries = response.data.deliveries || [];
      
      // Check for new deliveries or deletions (only after first load)
      if (!isFirstLoad.current && newDeliveries.length > 0) {
        const currentIds = new Set(newDeliveries.map(d => d.id));
        const newEntries = newDeliveries.filter(d => !previousDeliveryIds.current.has(d.id));
        
        // Check for deleted entries
        const deletedEntries = [...previousDeliveryIds.current].filter(id => !currentIds.has(id));
        
        if (newEntries.length > 0) {
          console.log('[GoodsInWarehouse] New delivery detected:', newEntries[0].supplier);
          // Show animation for the newest entry
          const newestEntry = newEntries[0]; // Most recent new entry
          setNewDeliveryInfo({
            supplier: newestEntry.supplier || 'New Delivery',
            order_number: newestEntry.order_number || '',
            action: 'added'
          });
          setShowDeliveryAnimation(true);
        } else if (deletedEntries.length > 0) {
          console.log('[GoodsInWarehouse] Delivery deleted, refreshing...');
          // Show a brief notification for deletion (no animation, just refresh)
          // The list will update automatically
        }
        
        // Update tracked IDs and persist to localStorage
        previousDeliveryIds.current = currentIds;
        try {
          localStorage.setItem('goods_in_delivery_ids', JSON.stringify([...currentIds]));
        } catch (e) {
          console.log('Could not save delivery IDs to storage');
        }
      } else if (isFirstLoad.current) {
        // First load - just track the IDs without animation
        const initialIds = new Set(newDeliveries.map(d => d.id));
        previousDeliveryIds.current = initialIds;
        isFirstLoad.current = false;
        try {
          localStorage.setItem('goods_in_delivery_ids', JSON.stringify([...initialIds]));
        } catch (e) {
          console.log('Could not save delivery IDs to storage');
        }
      } else if (!isFirstLoad.current && newDeliveries.length === 0 && previousDeliveryIds.current.size > 0) {
        // All deliveries were deleted
        previousDeliveryIds.current = new Set();
        try {
          localStorage.setItem('goods_in_delivery_ids', JSON.stringify([]));
        } catch (e) {
          console.log('Could not save delivery IDs to storage');
        }
      }
      
      // Separate regular deliveries from transfers
      setDeliveries(newDeliveries.filter(d => !d.is_transfer));
      setTransfers(newDeliveries.filter(d => d.is_transfer));
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
      setDeliveries([]);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveBranchId]);
  
  // Handle animation completion
  const handleAnimationComplete = useCallback(() => {
    setShowDeliveryAnimation(false);
    setNewDeliveryInfo(null);
  }, []);

  // Listen for new delivery events from office page via localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'goods_in_new_delivery') {
        try {
          const eventData = JSON.parse(e.newValue);
          if (eventData && eventData.type === 'new_delivery') {
            console.log('[GoodsInWarehouse] New delivery event received:', eventData);
            // Show animation immediately
            setNewDeliveryInfo({
              supplier: eventData.supplier || 'New Delivery',
              order_number: eventData.order_number || '',
              action: 'added'
            });
            setShowDeliveryAnimation(true);
            // Refresh the list
            fetchDeliveries();
          }
        } catch (err) {
          console.log('Error parsing new delivery event:', err);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchDeliveries]);

  useEffect(() => {
    fetchDeliveries();
    // Auto-refresh every 15 seconds for TV display (faster refresh for real-time updates)
    const interval = setInterval(fetchDeliveries, 15000);
    
    // Also refresh when page becomes visible again (handles background tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchDeliveries();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchDeliveries]);

  // Filter deliveries for today
  const todayDeliveries = deliveries.filter(d => isSameDay(new Date(d.date), today));

  // Get deliveries for a specific date
  const getDeliveriesForDate = (date) => {
    return deliveries.filter(d => isSameDay(new Date(d.date), date));
  };

  // Calculate max deliveries across all week days for uniform card height
  const maxDeliveriesInWeek = useMemo(() => {
    return Math.max(
      ...workingWeekDates.map(date => 
        getDeliveriesForDate(date).filter(d => !isWeekend(new Date(d.date))).length
      ),
      1 // At least 1 to avoid 0
    );
  }, [workingWeekDates, deliveries]);

  // Open detail modal (view only)
  const openDetailModal = (delivery) => {
    setSelectedDelivery(delivery);
    setWarehouseNote('');
    setShowDetailModal(true);
  };

  // Add warehouse note
  const handleAddNote = async () => {
    if (!warehouseNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const existingNotes = selectedDelivery.notes || '';
      const newNotes = existingNotes 
        ? `${existingNotes}\n[W/H ${format(new Date(), 'dd/MM HH:mm')}] ${warehouseNote}`
        : `[W/H ${format(new Date(), 'dd/MM HH:mm')}] ${warehouseNote}`;

      await api.put(`${API}/goods-in/deliveries/${selectedDelivery.id}`, {
        ...selectedDelivery,
        notes: newNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Note added successfully');
      setWarehouseNote('');
      fetchDeliveries();
      setSelectedDelivery({ ...selectedDelivery, notes: newNotes });
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    }
  };

  // Update status
  const handleUpdateStatus = async (status) => {
    if (status === 'failed') {
      setPendingStatus('failed');
      setShowFailedWarning(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.patch(`${API}/goods-in/deliveries/${selectedDelivery.id}/status`, {
        status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Delivery marked as ${status}`);
      setShowDetailModal(false);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Clear status (reset to pending)
  const handleClearStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.patch(`${API}/goods-in/deliveries/${selectedDelivery.id}/status`, {
        status: 'pending'  // Use 'pending' instead of null to clear status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Status cleared');
      setShowDetailModal(false);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to clear status:', error);
      toast.error('Failed to clear status');
    }
  };

  // Confirm failed status
  const confirmFailedStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.patch(`${API}/goods-in/deliveries/${selectedDelivery.id}/status`, {
        status: 'failed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Delivery marked as failed');
      setShowFailedWarning(false);
      setPendingStatus(null);
      setShowDetailModal(false);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Get status styling
  const getStatusColor = (status) => {
    if (status === 'complete') return '#22c55e';
    if (status === 'failed') return '#ef4444';
    return enconOrange;
  };

  // Get transfers for a specific date
  // Transfers stay on their booked date - they do NOT move forward
  const getTransfersForDate = (date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    return transfers.filter(t => {
      const transferDate = new Date(t.date);
      transferDate.setHours(0, 0, 0, 0);
      
      // If complete, show on completed date
      if (t.status === 'complete') {
        const completedDate = new Date(t.status_updated_at || t.date);
        completedDate.setHours(0, 0, 0, 0);
        return isSameDay(completedDate, checkDate);
      }
      
      // All transfers stay on their original booked date
      return isSameDay(transferDate, checkDate);
    });
  };

  // Group transfers by branch AND stock direction (so IN and OUT from same branch show separately)
  const groupTransfersByBranch = (transferList) => {
    const groups = {};
    transferList.forEach(t => {
      const branchName = t.supplier_name || 'Unknown';
      const direction = t.stock_direction || 'in';
      // Create unique key combining branch name and direction
      const key = `${branchName}::${direction}`;
      if (!groups[key]) {
        groups[key] = {
          branchName,
          direction,
          transfers: []
        };
      }
      groups[key].transfers.push(t);
    });
    return groups;
  };

  // Format creator name from email - show display name if available, otherwise first part of email
  const formatCreatorName = (createdBy) => {
    if (!createdBy) return '';
    // If it's an email, extract the part before @
    if (createdBy.includes('@')) {
      const namePart = createdBy.split('@')[0];
      // Capitalize first letter of each part separated by dots
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      ).join('.');
    }
    return createdBy;
  };

  // Get today's transfers (booked for today only)
  const todayTransfers = transfers.filter(t => {
    const transferDate = new Date(t.date);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    transferDate.setHours(0, 0, 0, 0);
    if (t.status === 'complete') {
      return isSameDay(new Date(t.status_updated_at || t.date), todayDate);
    }
    return isSameDay(transferDate, todayDate);
  });

  const upcomingTransfers = transfers.filter(t => {
    const transferDate = new Date(t.date);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    transferDate.setHours(0, 0, 0, 0);
    return t.status !== 'complete' && transferDate > todayDate;
  });

  // Past transfers: from the past 5 working days (before today)
  const pastTransfers = useMemo(() => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    let count = 0;
    let d = new Date(todayDate);
    while (count < 5) {
      d = addDays(d, -1);
      if (!isWeekend(d)) count++;
    }
    const cutoff = new Date(d);
    cutoff.setHours(0, 0, 0, 0);
    
    return transfers.filter(t => {
      const transferDate = new Date(t.date);
      transferDate.setHours(0, 0, 0, 0);
      if (t.status === 'complete') {
        const completedDate = new Date(t.status_updated_at || t.date);
        completedDate.setHours(0, 0, 0, 0);
        return completedDate < todayDate && completedDate >= cutoff;
      }
      return transferDate < todayDate && transferDate >= cutoff;
    });
  }, [transfers]);

  const [showPastTransfersModal, setShowPastTransfersModal] = useState(false);
  const [warehouseNotifications, setWarehouseNotifications] = useState([]);
  const notificationRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  
  // PI Count animation state
  const [showPICountAnimation, setShowPICountAnimation] = useState(false);
  const previousNotificationIds = useRef(new Set());
  const isFirstNotificationLoad = useRef(true);

  // Fetch warehouse notifications (PI counts at warehouse)
  const fetchWarehouseNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await api.get(`${API}/pi-counts/warehouse-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const notifications = resp.data.notifications || [];
      setWarehouseNotifications(notifications);
      
      // Check for new PI count notifications (trigger animation)
      const currentIds = new Set(notifications.map(n => n.id));
      if (!isFirstNotificationLoad.current) {
        const newIds = [...currentIds].filter(id => !previousNotificationIds.current.has(id));
        if (newIds.length > 0) {
          setShowPICountAnimation(true);
        }
      }
      isFirstNotificationLoad.current = false;
      previousNotificationIds.current = currentIds;
    } catch (err) {
      console.error('Failed to fetch warehouse notifications:', err);
    }
  }, []);

  useEffect(() => {
    fetchWarehouseNotifications();
    const interval = setInterval(fetchWarehouseNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchWarehouseNotifications]);

  // Check if notification text overflows and needs scrolling
  useEffect(() => {
    if (notificationRef.current) {
      const el = notificationRef.current;
      setNeedsScroll(el.scrollWidth > el.clientWidth);
    }
  }, [warehouseNotifications]);

  // Build notification elements grouped by assigned_to (case-insensitive merge)
  const notificationElements = useMemo(() => {
    if (warehouseNotifications.length === 0) return null;
    const byPerson = {};
    const personCanonical = {};
    warehouseNotifications.forEach(n => {
      const person = n.assigned_to || 'Unassigned';
      const key = person.toLowerCase().trim();
      if (!byPerson[key]) {
        byPerson[key] = [];
        personCanonical[key] = person;
      }
      byPerson[key].push(n);
    });
    const entries = Object.entries(byPerson);
    return entries.map(([key, counts], idx) => (
      <span key={key} className="inline-flex items-center">
        <span style={{ color: '#ee7203', fontWeight: 800, textTransform: 'uppercase' }}>{personCanonical[key]}:</span>
        <span className="ml-1.5 font-normal text-white">
          {counts.map((c, i) => (
            <span key={i}>{c.supplier_name} ({c.item_count}){i < counts.length - 1 ? ', ' : ''}</span>
          ))}
        </span>
        {idx < entries.length - 1 && (
          <span className="text-gray-400 font-bold" style={{ margin: '0 24px' }}>||</span>
        )}
      </span>
    ));
  }, [warehouseNotifications]);

  // Plain text version for overflow detection
  const notificationText = useMemo(() => {
    if (warehouseNotifications.length === 0) return '';
    const byPerson = {};
    warehouseNotifications.forEach(n => {
      const person = n.assigned_to || 'Unassigned';
      const key = person.toLowerCase().trim();
      if (!byPerson[key]) byPerson[key] = [];
      byPerson[key].push(n);
    });
    return Object.entries(byPerson).map(([, counts]) => {
      return counts.map(c => `${c.supplier_name} (${c.item_count})`).join(', ');
    }).join('  ||  ');
  }, [warehouseNotifications]);

  // Calendar helpers
  const getCalendarDays = () => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const endWeek = addDays(startOfWeek(end, { weekStartsOn: 1 }), 6);
    return eachDayOfInterval({ start: startWeek, end: endWeek });
  };

  // Format ordinal suffix for date
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // View Toggle Component for Header
  const ViewToggle = () => (
    <div className="flex rounded-full overflow-hidden border-2" style={{ borderColor: accentColor }}>
      <button
        onClick={() => setViewMode('week')}
        className="px-4 py-1.5 font-semibold text-sm transition-all"
        style={{ 
          backgroundColor: viewMode === 'week' ? accentColor : 'white',
          color: viewMode === 'week' ? 'white' : accentColor
        }}
      >
        Week
      </button>
      <button
        onClick={() => setViewMode('month')}
        className="px-4 py-1.5 font-semibold text-sm transition-all"
        style={{ 
          backgroundColor: viewMode === 'month' ? accentColor : 'white',
          color: viewMode === 'month' ? 'white' : accentColor
        }}
      >
        Month
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: limitedShell?.active ? 'transparent' : '#1a1a1a' }}>
      {/* Marquee animation for scrolling notifications */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      {/* Delivery Animation Overlay */}
      <DeliveryAnimationOverlay 
        isVisible={showDeliveryAnimation}
        newDeliveryInfo={newDeliveryInfo}
        onAnimationComplete={handleAnimationComplete}
      />
      
      {/* PI Count Notification Animation */}
      <PICountAnimationOverlay
        isVisible={showPICountAnimation}
        onAnimationComplete={() => setShowPICountAnimation(false)}
      />
      
      {/* Global Header - with view toggle */}
      <AppHeader 
        user={user}
        onLogout={() => {}}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <Package className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Goods In — Warehouse</span>
          </div>
        }
        subHeaderRight={
          <>
            {/* Week/Month Toggle in Header */}
            <ViewToggle />
          </>
        }
        mobileMenuButtons={[
          {
            icon: CalendarIcon,
            label: viewMode === 'week' ? 'Switch to Month View' : 'Switch to Week View',
            onClick: () => setViewMode(viewMode === 'week' ? 'month' : 'week'),
            color: accentColor
          }
        ]}
      />

      {/* Main Content - moved up under header */}
      <div className="flex-1 px-3 md:px-6 lg:px-10 pb-2 pt-[20px] bg-gray-100 min-h-screen">
        
        {/* Notification Bar - PI Counts at Warehouse */}
        {warehouseNotifications.length > 0 ? (
          <div className="flex mb-2 rounded-xl overflow-hidden" style={{ border: '2px solid #d4a017' }} data-testid="warehouse-notification-bar">
            {/* Yellow side tab */}
            <div 
              className="flex items-center justify-center px-3"
              style={{ backgroundColor: '#d4a017', minWidth: '36px' }}
            >
              <AlertTriangle className="w-5 h-5 text-gray-900" />
            </div>
            
            {/* Notification content */}
            <div className="flex-1 bg-gray-800 px-4 py-1.5 overflow-hidden relative">
              <div 
                ref={notificationRef}
                className="whitespace-nowrap text-sm text-white"
                style={needsScroll ? {
                  display: 'inline-block',
                  animation: 'marquee 20s linear infinite',
                  paddingRight: '100%'
                } : {}}
              >
                {notificationElements}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center mb-2" data-testid="no-notifications-bar">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-white text-sm font-medium">No Notifications</span>
            </div>
          </div>
        )}
        
        {/* Transfers In Section - Title on left side */}
        {(todayTransfers.length > 0 || upcomingTransfers.length > 0 || pastTransfers.length > 0) && (
          <div className="flex mb-4">
            {/* Vertical Title - rounded corners face outward (toward page edge) */}
            {/* Note: Using rounded-r because element is rotated 180deg */}
            <div 
              className="flex items-center justify-center bg-blue-600 rounded-r-2xl px-3"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
            >
              <span className="text-white font-bold text-sm tracking-wider flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5" />
                TRANSFERS
              </span>
            </div>
            
            {/* Transfers Content - rounded corners on right edge */}
            <div className="flex-1 bg-gray-800 rounded-r-2xl p-4">
              <div className="flex gap-4 overflow-x-auto pb-1 items-center">

                {/* Past Week's Transfers Button - always first */}
                {pastTransfers.length > 0 && (
                  <div
                    onClick={() => setShowPastTransfersModal(true)}
                    className="flex-shrink-0 w-64 p-4 rounded-xl border-2 transition-all hover:shadow-lg hover:scale-[1.02] cursor-pointer flex flex-col items-center justify-center"
                    style={{ borderColor: enconOrange, backgroundColor: 'rgba(238, 114, 3, 0.15)', minHeight: '110px' }}
                    data-testid="past-transfers-btn"
                  >
                    <Clock className="w-7 h-7 mb-1" style={{ color: enconOrange }} />
                    <span className="font-bold text-sm text-center" style={{ color: enconOrange }}>
                      Past Week's Transfers
                    </span>
                    <span className="text-white text-sm font-bold px-3 py-0.5 rounded-full mt-1" style={{ backgroundColor: enconOrange }}>
                      {pastTransfers.length}
                    </span>
                  </div>
                )}

                {/* Separator after Past Transfers */}
                {pastTransfers.length > 0 && (todayTransfers.length > 0 || upcomingTransfers.length > 0) && (
                  <div className="flex-shrink-0 flex items-center px-1">
                    <div className="text-2xl text-gray-600 font-light">||</div>
                  </div>
                )}

                {/* Today's Transfers - Grouped by Branch + Direction */}
                {Object.entries(groupTransfersByBranch(todayTransfers)).map(([groupKey, group]) => {
                  const { branchName, direction, transfers: branchTransfers } = group;
                  // Card color based on direction (all in group have same direction)
                  const cardColor = branchTransfers.every(t => t.status === 'complete') ? '#22c55e' : 
                                   branchTransfers.some(t => t.status === 'failed') ? '#ef4444' : 
                                   direction === 'out' ? enconPink : '#3b82f6';
                  const bgColor = branchTransfers.every(t => t.status === 'complete') ? 'rgba(34, 197, 94, 0.2)' :
                                 branchTransfers.some(t => t.status === 'failed') ? 'rgba(239, 68, 68, 0.2)' :
                                 direction === 'out' ? 'rgba(236, 72, 153, 0.15)' : 'rgba(59, 130, 246, 0.15)';
                  
                  return (
                    <div
                      key={`today-${groupKey}`}
                      onClick={() => branchTransfers.length > 1 ? setExpandedTransferGroup({ name: branchName, transfers: branchTransfers }) : openDetailModal(branchTransfers[0])}
                      className="flex-shrink-0 w-64 p-4 rounded-xl border-2 transition-all hover:scale-[1.02] cursor-pointer relative"
                      style={{ 
                        borderColor: cardColor,
                        backgroundColor: bgColor
                      }}
                    >
                      {branchTransfers.length > 1 && (
                        <span 
                          className="absolute top-2 right-2 text-white text-sm font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: cardColor }}
                        >
                          {branchTransfers.length}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        {branchTransfers.every(t => t.status === 'complete') && <Check className="w-5 h-5 text-green-500" />}
                        {branchTransfers.some(t => t.status === 'failed') && <XCircle className="w-5 h-5 text-red-500" />}
                        {!branchTransfers.every(t => t.status === 'complete') && !branchTransfers.some(t => t.status === 'failed') && (
                          <ArrowRightLeft className="w-5 h-5" style={{ color: cardColor }} />
                        )}
                        <span className="font-bold text-lg text-white truncate">
                          {branchName}
                        </span>
                      </div>
                      <div className="text-gray-400 text-sm">
                        {branchTransfers.length === 1 ? (branchTransfers[0].order_number ? `Expense: #${branchTransfers[0].order_number}` : '') : `${branchTransfers.length} transfers`}
                      </div>
                      {/* Creator name for single transfer */}
                      {branchTransfers.length === 1 && branchTransfers[0].created_by && (
                        <div className="text-xs text-gray-500">
                          Added by: {formatCreatorName(branchTransfers[0].created_by)}
                        </div>
                      )}
                      {/* Stock IN/OUT Tag - always show since grouped by direction */}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-semibold text-sm" style={{ color: cardColor }}>Today</span>
                        {!branchTransfers.every(t => t.status === 'complete') && !branchTransfers.some(t => t.status === 'failed') && (
                          <span 
                            className="text-xs font-bold px-1.5 py-0.5 rounded text-white"
                            style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                          >
                            STOCK {direction === 'out' ? 'OUT' : 'IN'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Separator between Today and Upcoming */}
                {todayTransfers.length > 0 && upcomingTransfers.length > 0 && (
                  <div className="flex-shrink-0 flex items-center px-1">
                    <div className="text-2xl text-gray-600 font-light">||</div>
                  </div>
                )}
                
                {/* Upcoming Transfers - Grouped by Branch + Direction (Grey cards, colored tags) */}
                {Object.entries(groupTransfersByBranch(upcomingTransfers)).map(([groupKey, group]) => {
                  const { branchName, direction, transfers: branchTransfers } = group;
                  
                  return (
                    <div
                      key={`upcoming-${groupKey}`}
                      onClick={() => branchTransfers.length > 1 ? setExpandedTransferGroup({ name: branchName, transfers: branchTransfers }) : openDetailModal(branchTransfers[0])}
                      className="flex-shrink-0 w-64 p-4 rounded-xl border-2 transition-all hover:scale-[1.02] cursor-pointer relative opacity-60"
                      style={{ 
                        borderColor: '#6b7280',
                        backgroundColor: 'rgba(107, 114, 128, 0.15)'
                      }}
                    >
                      {branchTransfers.length > 1 && (
                        <span className="absolute top-2 right-2 bg-gray-500 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                          {branchTransfers.length}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowRightLeft className="w-5 h-5 text-gray-400" />
                        <span className="font-bold text-lg text-gray-300 truncate">
                          {branchName}
                        </span>
                      </div>
                      <div className="text-gray-500 text-sm">
                        {branchTransfers.length === 1 ? (branchTransfers[0].order_number ? `Expense: #${branchTransfers[0].order_number}` : '') : `${branchTransfers.length} transfers`}
                      </div>
                      {/* Creator name for single transfer */}
                      {branchTransfers.length === 1 && branchTransfers[0].created_by && (
                        <div className="text-xs text-gray-500">
                          Added by: {formatCreatorName(branchTransfers[0].created_by)}
                        </div>
                      )}
                      {/* Stock IN/OUT Tag with correct color on grey card */}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-400 text-sm">
                          {format(new Date(branchTransfers[0].date), 'EEE dd/MM')}
                        </span>
                        <span 
                          className="text-xs font-bold px-1.5 py-0.5 rounded text-white"
                          style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                        >
                          STOCK {direction === 'out' ? 'OUT' : 'IN'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Week View - Title on left side */}
        {viewMode === 'week' && (
          <div className="flex">
            {/* Vertical Title - rounded corners face outward (toward page edge) */}
            {/* Note: Using rounded-r because element is rotated 180deg */}
            <div 
              className="flex items-center justify-center rounded-r-2xl px-3"
              style={{ 
                writingMode: 'vertical-rl', 
                textOrientation: 'mixed', 
                transform: 'rotate(180deg)',
                backgroundColor: accentColor
              }}
            >
              <span className="text-white font-bold text-sm tracking-wider flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                WEEK VIEW
              </span>
            </div>
            
            {/* Week Content - rounded corners on right edge */}
            <div className="flex-1 bg-gray-900 rounded-r-2xl p-2">
              <div className="overflow-x-auto pb-1 snap-x snap-mandatory md:snap-none">
                <div className="flex md:grid md:grid-cols-5 gap-2 min-w-max md:min-w-0">
                  {workingWeekDates.map((date, idx) => {
                    const dayDeliveries = getDeliveriesForDate(date).filter(d => !isWeekend(new Date(d.date)));
                    const dayTransfers = getTransfersForDate(date);
                    const isToday = isSameDay(date, today);
                    const dayNum = date.getDate();
                    
                    // Determine if this day is in a different week than today
                    const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
                    const dateWeekStart = startOfWeek(date, { weekStartsOn: 1 });
                    const isNextWeek = dateWeekStart > todayWeekStart;
                    
                    // Header color: accent color for today, lighter grey for next week, dark grey for current week
                    const headerColor = isToday ? accentColor : (isNextWeek ? '#4b5563' : '#374151');
                    
                    // Group transfers by branch for this day
                    const groupedDayTransfers = groupTransfersByBranch(dayTransfers);
                    
                    return (
                      <div
                        key={idx}
                        className="bg-gray-800 rounded-xl overflow-hidden flex flex-col snap-center w-[210px] flex-shrink-0 md:w-auto md:flex-shrink"
                      >
                        {/* Day Header */}
                        <div 
                          className="px-2 py-1.5 text-center font-bold text-white"
                          style={{ backgroundColor: headerColor }}
                        >
                          <div className="text-base">
                            {format(date, 'EEE')} {dayNum}{getOrdinalSuffix(dayNum)} {format(date, 'MMM')}
                          </div>
                        </div>
                      
                      {/* Deliveries & Transfers List */}
                      <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                        {/* Transfer cards - Slimmer style grouped by branch + direction */}
                        {Object.entries(groupedDayTransfers).map(([groupKey, group]) => {
                          const { branchName, direction, transfers: branchTransfers } = group;
                          // Card color based on direction (all in group have same direction)
                          const cardColor = branchTransfers.every(t => t.status === 'complete') ? '#22c55e' : 
                                           branchTransfers.some(t => t.status === 'failed') ? '#ef4444' : 
                                           direction === 'out' ? enconPink : '#3b82f6';
                          const bgColor = branchTransfers.every(t => t.status === 'complete') ? 'rgba(34, 197, 94, 0.2)' :
                                         branchTransfers.some(t => t.status === 'failed') ? 'rgba(239, 68, 68, 0.2)' :
                                         direction === 'out' ? 'rgba(236, 72, 153, 0.15)' : 'rgba(59, 130, 246, 0.15)';
                          
                          return (
                            <div
                              key={`transfer-${groupKey}`}
                              onClick={() => branchTransfers.length > 1 ? setExpandedTransferGroup({ name: branchName, transfers: branchTransfers }) : openDetailModal(branchTransfers[0])}
                              className="px-3 py-2.5 rounded-xl cursor-pointer hover:scale-[1.01] transition-all"
                              style={{
                                borderLeftWidth: '4px',
                                borderLeftColor: cardColor,
                                backgroundColor: bgColor
                              }}
                            >
                              <div className="flex items-center gap-1.5">
                                <ArrowRightLeft className="w-4 h-4 flex-shrink-0" style={{ color: cardColor }} />
                                <span className="text-sm font-semibold truncate flex-1" style={{ color: direction === 'out' ? '#5eead4' : '#93c5fd' }}>{branchName}</span>
                                {/* Creator name inline */}
                                {branchTransfers.length === 1 && branchTransfers[0].created_by && (
                                  <span className="text-xs text-gray-500 flex-shrink-0">
                                    {formatCreatorName(branchTransfers[0].created_by)}
                                  </span>
                                )}
                                {/* Stock IN/OUT Tag inline */}
                                {!branchTransfers.every(t => t.status === 'complete') && !branchTransfers.some(t => t.status === 'failed') && (
                                  <span 
                                    className="text-xs font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0"
                                    style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                                  >
                                    {direction === 'out' ? 'OUT' : 'IN'}
                                  </span>
                                )}
                                {branchTransfers.length > 1 && (
                                  <span 
                                    className="text-xs text-white px-1.5 py-0.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: cardColor }}
                                  >
                                    {branchTransfers.length}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Regular delivery cards */}
                        {dayDeliveries.length === 0 && Object.keys(groupedDayTransfers).length === 0 ? (
                          <div className="text-gray-500 text-center py-8 text-lg">
                            No deliveries
                          </div>
                        ) : (
                          dayDeliveries.map((delivery) => (
                            <div
                              key={delivery.id}
                              onClick={() => openDetailModal(delivery)}
                              className="px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:scale-[1.02]"
                              style={{ 
                                borderLeftWidth: '4px',
                                borderLeftColor: getStatusColor(delivery.status),
                                backgroundColor: delivery.status === 'complete' ? 'rgba(34, 197, 94, 0.2)' :
                                               delivery.status === 'failed' ? 'rgba(239, 68, 68, 0.2)' :
                                               'rgba(255, 255, 255, 0.1)'
                              }}
                            >
                              <div className="flex items-center gap-1.5">
                                {delivery.status === 'complete' && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
                                {delivery.status === 'failed' && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                                <span className="font-bold text-base text-white truncate">
                                  {delivery.supplier_name}
                                </span>
                                {delivery.source === 'purchase_order_email' && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: '#dcfce7', color: '#15803d' }}>PO</span>
                                )}
                              </div>
                              {delivery.order_number && (
                                <div className="text-gray-400 text-sm leading-tight">
                                  #{delivery.order_number}
                                </div>
                              )}
                              {delivery.created_by && (
                                <div className="text-gray-500 text-sm leading-tight">
                                  Added by: {formatCreatorName(delivery.created_by)}
                                </div>
                              )}
                              {delivery.notes && (
                                <div className="text-gray-500 text-sm leading-tight line-clamp-1 italic">
                                  {delivery.notes.split('\n')[0]}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Month View - Full Calendar */}
        {viewMode === 'month' && (
          <div className="bg-gray-800 rounded-2xl p-6 mb-8">
            {/* Calendar Header with integrated toggle */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                  className="p-3 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="w-8 h-8 text-white" />
                </button>
                
                {/* Week toggle button - left of title */}
                <button
                  onClick={() => setViewMode('week')}
                  className="px-5 py-2 rounded-full font-semibold transition-all border-2"
                  style={{ 
                    backgroundColor: 'white',
                    borderColor: enconOrange,
                    color: enconOrange
                  }}
                >
                  Week
                </button>
              </div>
              
              <h3 className="text-3xl font-bold text-white">
                {format(calendarMonth, 'MMMM yyyy')}
              </h3>
              
              <div className="flex items-center gap-4">
                {/* Month toggle button - right of title, highlighted orange */}
                <button
                  onClick={() => setViewMode('month')}
                  className="px-5 py-2 rounded-full font-semibold transition-all text-white"
                  style={{ backgroundColor: enconOrange }}
                >
                  Month
                </button>
                
                <button
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                  className="p-3 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day Headers */}
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className={`text-center text-lg font-bold py-3 ${
                  day === 'Sat' || day === 'Sun' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {getCalendarDays().map((date, idx) => {
                const dayDeliveries = getDeliveriesForDate(date);
                const isCurrentMonth = isSameMonth(date, calendarMonth);
                const isToday = isSameDay(date, today);
                const isWeekendDay = isWeekend(date);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[100px] p-2 rounded-xl transition-all ${
                      isCurrentMonth ? 'bg-gray-700' : 'bg-gray-800'
                    } ${isToday ? 'ring-2' : ''} ${
                      isWeekendDay ? 'opacity-50' : ''
                    }`}
                    style={{ ringColor: enconOrange }}
                  >
                    <div className={`text-sm font-bold mb-2 ${
                      isToday ? 'text-orange-400' : isCurrentMonth ? 'text-white' : 'text-gray-500'
                    }`}>
                      {format(date, 'd')}
                    </div>
                    
                    <div className="space-y-1">
                      {dayDeliveries.slice(0, 2).map((delivery) => (
                        <div
                          key={delivery.id}
                          onClick={() => openDetailModal(delivery)}
                          className="text-xs px-2 py-1 rounded truncate text-white cursor-pointer hover:opacity-80 flex items-center gap-1"
                          style={{ backgroundColor: getStatusColor(delivery.status) }}
                        >
                          {delivery.status === 'complete' && <Check className="w-3 h-3" />}
                          {delivery.status === 'failed' && <XCircle className="w-3 h-3" />}
                          {delivery.supplier_name}
                          {delivery.source === 'purchase_order_email' && (
                            <span className="text-[8px] font-bold ml-0.5 px-1 py-0.5 rounded" style={{ background: '#dcfce7', color: '#15803d' }}>PO</span>
                          )}
                        </div>
                      ))}
                      {dayDeliveries.length > 2 && (
                        <div className="text-xs text-gray-400 text-center">
                          +{dayDeliveries.length - 2}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal (View Only with Status Actions) - Gradient Border */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent 
          className={`mx-4 shadow-2xl p-0 overflow-hidden ${selectedDelivery?.purchase_order_data ? 'max-w-4xl' : 'max-w-lg'}`}
          style={{ 
            background: 'linear-gradient(135deg, #fb923c 0%, #ec4899 100%)',
            borderRadius: '1rem'
          }}
        >
          <div className="bg-white m-[3px] rounded-[13px] p-6">
            <DialogHeader className="pr-12">
              <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ color: enconBlack }}>
                <Package className="w-6 h-6" style={{ color: getStatusColor(selectedDelivery?.status) }} />
                Delivery Details
                {selectedDelivery?.status && (
                  <span className={`ml-2 px-2 py-1 rounded text-sm text-white ${
                    selectedDelivery.status === 'complete' ? 'bg-green-500' :
                    selectedDelivery.status === 'failed' ? 'bg-red-500' : ''
                  }`}>
                    {selectedDelivery.status.charAt(0).toUpperCase() + selectedDelivery.status.slice(1)}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedDelivery && (
              <div className={selectedDelivery.purchase_order_data ? 'flex' : 'space-y-4 mt-4'}>
                {/* ── Left column: delivery details + actions ─────────────── */}
                <div
                  className="space-y-4 mt-4 overflow-y-auto flex-shrink-0"
                  style={selectedDelivery.purchase_order_data ? { width: '380px', maxHeight: '80vh' } : {}}
                >
                {/* Delivery Info */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
                  <div>
                    <span className="text-gray-500 text-sm">Date</span>
                    <p className="font-semibold">{format(new Date(selectedDelivery.date), 'EEEE, do MMMM yyyy')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Supplier</span>
                    <p className="font-bold text-lg" style={{ color: enconOrange }}>{selectedDelivery.supplier_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Order Number</span>
                    <p className="font-semibold">#{selectedDelivery.order_number}</p>
                  </div>
                  {selectedDelivery.notes && (
                    <div>
                      <span className="text-gray-500 text-sm">Notes</span>
                      <p className="text-gray-700 whitespace-pre-line">{selectedDelivery.notes}</p>
                    </div>
                  )}
                </div>

                {/* Add W/H Note */}
                <div>
                  <label className="block text-sm font-semibold mb-1 flex items-center gap-1" style={{ color: enconBlack }}>
                    <MessageSquare className="w-4 h-4" />
                    Add Warehouse Note
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      placeholder="Add a note from warehouse..."
                      value={warehouseNote}
                      onChange={(e) => setWarehouseNote(e.target.value)}
                      rows={2}
                      className="flex-1 px-3 py-2 rounded-lg border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none resize-none text-sm"
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!warehouseNote.trim()}
                      className="px-4 text-white"
                      style={{ backgroundColor: enconOrange }}
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Status Actions - Always show, allow changing */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-600">Update Status:</div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleUpdateStatus('complete')}
                      className={`flex-1 py-3 font-semibold flex items-center justify-center gap-2 ${
                        selectedDelivery.status === 'complete' 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-green-500 hover:bg-green-600 text-white'
                      }`}
                    >
                      <Check className="w-5 h-5" />
                      {selectedDelivery.status === 'complete' ? 'Completed ✓' : 'Mark Complete'}
                    </Button>
                    <Button
                      onClick={() => handleUpdateStatus('failed')}
                      className={`flex-1 py-3 font-semibold flex items-center justify-center gap-2 ${
                        selectedDelivery.status === 'failed' 
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                    >
                      <XCircle className="w-5 h-5" />
                      {selectedDelivery.status === 'failed' ? 'Failed ✓' : 'Mark Failed'}
                    </Button>
                  </div>
                  
                  {/* Clear Status Button - Only show if has status */}
                  {selectedDelivery.status && (
                    <Button
                      onClick={handleClearStatus}
                      variant="outline"
                      className="w-full py-2 text-gray-600 border-gray-300 hover:bg-gray-100"
                    >
                      Clear Status (Reset to Pending)
                    </Button>
                  )}
                </div>

                {/* Purchase Order parsed data moved to right column */}

                {/* Close Button */}
                <Button
                  variant="outline"
                  onClick={() => setShowDetailModal(false)}
                  className="w-full py-3"
                >
                  Close
                </Button>
                </div>{/* end left column */}

                {/* ── Right column: parsed PO details ─────────────────────── */}
                {selectedDelivery.purchase_order_data && (
                  <div
                    className="flex-1 min-w-[320px] px-5 pb-5 pt-4 overflow-y-auto border-l border-gray-200 bg-gray-50/30"
                    style={{ maxHeight: '80vh' }}
                  >
                    <GoodsInPOPanel data={selectedDelivery.purchase_order_data} noTopBorder />
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Failed Warning Dialog - Gradient Border */}
      <Dialog open={showFailedWarning} onOpenChange={setShowFailedWarning}>
        <DialogContent 
          className="max-w-md mx-4 shadow-2xl p-0 overflow-hidden"
          style={{ 
            background: 'linear-gradient(135deg, #fb923c 0%, #ec4899 100%)',
            borderRadius: '1rem'
          }}
        >
          <div className="bg-white m-[3px] rounded-[13px] p-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Important Notice
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 p-4 bg-red-50 rounded-xl border-2 border-red-200">
              <p className="text-red-700 font-semibold">
                ⚠️ Please inform the office immediately about this failed delivery.
              </p>
              <p className="text-red-600 mt-2 text-sm">
                The office needs to be aware so they can arrange a re-delivery or take appropriate action.
              </p>
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowFailedWarning(false);
                  setPendingStatus(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmFailedStatus}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                I Understand - Mark as Failed
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expanded Transfer Group Modal */}
      <Dialog open={!!expandedTransferGroup} onOpenChange={() => setExpandedTransferGroup(null)}>
        <DialogContent 
          className="max-w-md mx-4 shadow-2xl p-0 overflow-hidden"
          style={{ 
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            borderRadius: '1rem'
          }}
        >
          <div className="bg-gray-900 m-[3px] rounded-[13px] p-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2 text-blue-400">
                <ArrowRightLeft className="w-6 h-6" />
                {expandedTransferGroup?.name}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-400 -mt-2">
              {expandedTransferGroup?.transfers?.length} transfers from this branch
            </p>
            <div className="space-y-2 mt-4 max-h-[400px] overflow-y-auto">
              {expandedTransferGroup?.transfers?.map((transfer) => (
                <div
                  key={transfer.id}
                  onClick={() => {
                    setExpandedTransferGroup(null);
                    openDetailModal(transfer);
                  }}
                  className="p-3 rounded-lg border-l-4 cursor-pointer hover:bg-gray-700 transition-all"
                  style={{
                    borderLeftColor: transfer.status === 'complete' ? '#22c55e' : 
                                    transfer.status === 'failed' ? '#ef4444' : '#3b82f6',
                    backgroundColor: transfer.status === 'complete' ? 'rgba(34, 197, 94, 0.15)' :
                                    transfer.status === 'failed' ? 'rgba(239, 68, 68, 0.15)' :
                                    'rgba(59, 130, 246, 0.1)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {transfer.status === 'complete' && <Check className="w-4 h-4 text-green-500" />}
                      {transfer.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                      <span className="font-semibold text-sm text-white">#{transfer.order_number}</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {format(new Date(transfer.date), 'EEE dd/MM')}
                    </span>
                  </div>
                  {transfer.carrier_details && (
                    <div className="text-xs text-gray-400 mt-1 truncate">
                      {transfer.carrier_details}
                    </div>
                  )}
                  {transfer.notes && (
                    <div className="text-xs text-gray-500 mt-1 italic truncate">
                      {transfer.notes.split('\n')[0]}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => setExpandedTransferGroup(null)}
              className="w-full mt-4 text-gray-300 border-gray-600 hover:bg-gray-700"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Past Week's Transfers Modal */}
      <Dialog open={showPastTransfersModal} onOpenChange={setShowPastTransfersModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: enconOrange }}>
              <Clock className="w-5 h-5" />
              Past Week's Transfers ({pastTransfers.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {(() => {
              const byDate = {};
              pastTransfers.forEach(t => {
                const d = t.status === 'complete' 
                  ? format(new Date(t.status_updated_at || t.date), 'yyyy-MM-dd')
                  : format(new Date(t.date), 'yyyy-MM-dd');
                if (!byDate[d]) byDate[d] = [];
                byDate[d].push(t);
              });
              const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
              
              return sortedDates.map(dateKey => {
                const dateTransfers = byDate[dateKey];
                const dateObj = new Date(dateKey);
                return (
                  <div key={dateKey}>
                    <h3 className="text-sm font-bold text-gray-400 mb-2 border-b border-gray-700 pb-1">
                      {format(dateObj, 'EEEE do MMMM')}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {dateTransfers.map(t => {
                        const direction = t.stock_direction || 'in';
                        const cardColor = t.status === 'complete' ? '#22c55e' : 
                                         t.status === 'failed' ? '#ef4444' : 
                                         direction === 'out' ? enconPink : '#3b82f6';
                        const bgColor = t.status === 'complete' ? 'rgba(34, 197, 94, 0.15)' :
                                       t.status === 'failed' ? 'rgba(239, 68, 68, 0.15)' :
                                       direction === 'out' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)';
                        return (
                          <div
                            key={t.id || t._id}
                            onClick={() => { setShowPastTransfersModal(false); openDetailModal(t); }}
                            className="p-3 rounded-xl border-2 cursor-pointer hover:shadow-md transition-all"
                            style={{ borderColor: cardColor, backgroundColor: bgColor }}
                            data-testid={`past-transfer-${t.id || t._id}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {t.status === 'complete' && <Check className="w-4 h-4 text-green-500" />}
                              {t.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                              {t.status !== 'complete' && t.status !== 'failed' && (
                                <ArrowRightLeft className="w-4 h-4" style={{ color: cardColor }} />
                              )}
                              <span className="font-bold text-sm truncate text-white">{t.supplier_name || 'Unknown'}</span>
                            </div>
                            {t.order_number && (
                              <div className="text-xs text-gray-400">Expense: {t.order_number}</div>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-semibold capitalize" style={{ color: cardColor }}>{t.status || 'pending'}</span>
                              <span 
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white"
                                style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                              >
                                STOCK {direction === 'out' ? 'OUT' : 'IN'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
            {pastTransfers.length === 0 && (
              <p className="text-center text-gray-500 py-6">No transfers from the past 5 working days</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsInWarehouse;
